import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "./Loading";
import orderService from "../Services/orderService";
import { RxCross2 } from "react-icons/rx";
import { IoIosCheckmark } from "react-icons/io";

const CustomerDetails = ({ isOpen, onClose, id }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen || !id) return;

      setLoading(true);
      setError(false);
      try {
        const res = await orderService.fetchCustomerOrders(id);
        console.log("Customer Ledger Response:", res); // 👈 check your actual field names here
        setRows(res?.orders || []);
      } catch (e) {
        console.error(e);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, id]);

  if (!isOpen) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB");
  };

  const formatAmount = (amount) => {
    if (amount == null) return "-";
    return Number(amount).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white w-[92%] max-w-5xl rounded-2xl shadow-xl p-6 relative">
        <div className="flex flex-row justify-between items-start">
          <div>
            <h1 className="font-bold text-3xl text-secondary">
              Customer Orders
            </h1>
          </div>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-red-600 duration-300 text-xl"
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div className="py-10">
            <Loading />
          </div>
        ) : error ? (
          <p className="text-red-600 font-semibold mt-4">
            Failed to load purchase details.
          </p>
        ) : rows.length === 0 ? (
          <p className="text-gray-500 mt-4">No purchase records found.</p>
        ) : (
          <div className="max-h-[60vh] overflow-auto border rounded-xl mt-10 no-scrollbar">
            <table className="w-full text-[12px]">
              <thead className="sticky top-0 bg-gray-800 border-b text-white">
                <tr className="text-left">
                  <th className="p-3">Sr No.</th>
                  <th className="p-3">Order No.</th>
                  <th className="p-3">Order Date</th>
                  <th className="p-3">Ref No.</th>
                  <th className="p-3 text-center">Status</th>
                  <th className="p-3 text-center">Order Status</th>
                  <th className="p-3 text-center">Purchase Status</th>
                  <th className="p-3 text-center">Amount</th>
                  <th className="p-3 text-center">Amount Incl VAT</th>
                </tr>
              </thead>

              <tbody>
                {[...rows].reverse()?.map((r, idx) => (
                  <tr
                    key={`${r.id}-${idx}`}
                    className="border-b hover:bg-gray-50"
                  >
                    {/* ✅ Sr No. */}
                    <td className="p-3 font-bold text-blue-500">{idx + 1}.</td>

                    {/* ✅ Order No. — adjust field name to match your API */}
                    <td className="p-3 font-semibold">
                      GAP-{r.order_number ?? r.order_no ?? r.id ?? "-"}
                    </td>

                    {/* ✅ Order Date */}
                    <td className="p-3">{formatDate(r.order_date)}</td>

                    {/* ✅ Ref No. */}
                    <td className="p-3">{r.reference_no ?? "-"}</td>

                    {/* ✅ Status */}
                    <td className="p-3 text-center">
                      <span
                        className={`uppercase font-semibold py-1 px-2 rounded-md text-[10px] ${
                          r.payment_status === "paid"
                            ? "bg-green-100 text-green-600"
                            : r.payment_status === "notpaid"
                              ? "bg-red-100 text-red-500"
                              : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {r.payment_status === "paid"
                          ? "Paid"
                          : r.payment_status === "notpaid"
                            ? "Unpaid"
                            : r.payment_status === "check_received"
                              ? "Check Received"
                              : "Partial"}
                      </span>
                    </td>

                    {/* ✅ Order Status */}
                    <td className="p-3 text-center">
                      <span
                        className={`uppercase font-semibold py-1 bg-opacity-20 px-2 rounded-md text-[10px] ${
                          r.status === "underprocess"
                            ? "text-red-500 bg-red-500"
                            : r.status === "readytodeliver"
                              ? "text-amber-500 bg-amber-500"
                              : r.status === "confirmed"
                                ? "text-blue-500 bg-blue-500"
                                : "text-green-500 bg-green-500"
                        }`}
                      >
                        {r.status === "pending"
                          ? "Pending"
                          : r.status === "confirmed"
                            ? "Confirmed"
                            : r.status === "underprocess"
                              ? "Under Process"
                              : r.status === "readytodeliver"
                                ? "Ready To Deliver"
                                : r.status === "dispatched"
                                  ? "Dispatched"
                                  : "Delivered"}
                      </span>
                    </td>

                    {/* ✅ Purchase Status */}
                    <td className="p-3 text-center">
                      <span>
                        {r.purchase === 0 ? (
                          <div className="flex w-fit justify-center items-center content-center text-white rounded-full bg-red-500 p-1">
                            <RxCross2 className="h-2 w-2" />
                          </div>
                        ) : (
                          <div className="flex w-fit justify-center items-center content-center text-white rounded-full bg-green-500">
                            <IoIosCheckmark className="h-4 w-4" />
                          </div>
                        )}
                      </span>
                    </td>

                    {/* ✅ Amount */}
                    <td className="p-3 text-center font-semibold">
                      {formatAmount(r.amount ?? r.total_amount)}
                    </td>

                    {/* ✅ Amount Incl VAT */}
                    <td className="p-3 text-center font-semibold">
                      {formatAmount(r.vat_amount)}
                    </td>

                    {/* ✅ View button */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetails;
